import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import { apiClient } from '@/lib/api-client';
import {
  NOTIFICATION_GATE_CATEGORIES,
  useClientNotificationGatesList,
  contactGateKeys,
  type ClientNotificationGates,
} from './hooks/useContactNotificationGates';

/**
 * NotificationSettingsPage — a single place to control, per associated client
 * and per category, whether that client receives notifications. Rows are the
 * logged-in user's clients (scoped server-side to assigned/team); columns are
 * the notification categories. A checked box = client receives that category.
 */
export function NotificationSettingsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: clients, isLoading } = useClientNotificationGatesList();
  const [search, setSearch] = useState('');
  // Tracks in-flight cell writes so we can disable them individually.
  const [savingCell, setSavingCell] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients ?? [];
    return (clients ?? []).filter(
      (c) => c.name.toLowerCase().includes(q) || (c.email ?? '').toLowerCase().includes(q),
    );
  }, [clients, search]);

  const isEnabled = (client: ClientNotificationGates, category: string) =>
    !(client.gates?.[category] === true);

  const toggle = async (client: ClientNotificationGates, category: string, enabled: boolean) => {
    const cellKey = `${client.contactId}:${category}`;
    setSavingCell(cellKey);
    try {
      // enabled -> disabled:false (deliver); unchecked -> disabled:true (suppress).
      await apiClient.put(`/contacts/${client.contactId}/notification-gates`, {
        gates: { [category]: !enabled },
      });
      // Refresh the list and the per-contact cache.
      await queryClient.invalidateQueries({ queryKey: contactGateKeys.list() });
      queryClient.invalidateQueries({ queryKey: contactGateKeys.detail(client.contactId) });
    } catch {
      toast({ variant: 'error', title: 'Could not update notification setting' });
    } finally {
      setSavingCell(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Notification Settings
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Control which notifications each of your clients receives. Uncheck a box to stop sending
          that category to that client.
        </p>
      </div>

      <Card padding="none">
        <CardHeader
          title="Clients"
          description="Only clients assigned to you (or your team) are shown."
          className="p-4"
        />
        <CardContent className="p-0">
          {/* Search */}
          <div className="border-b border-border p-4 dark:border-border-dark">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter clients by name or email…"
              aria-label="Filter clients"
              className="w-full max-w-sm rounded-md border border-border bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
            />
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
              {clients && clients.length > 0
                ? 'No clients match your filter.'
                : 'No clients are assigned to you.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full text-sm"
                role="grid"
                aria-label="Client notification settings"
              >
                <thead>
                  <tr className="border-b border-border bg-surface-secondary dark:border-border-dark dark:bg-surface-dark-tertiary">
                    <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">
                      Client
                    </th>
                    {NOTIFICATION_GATE_CATEGORIES.map((cat) => (
                      <th
                        key={cat.key}
                        className="px-3 py-3 text-center font-medium text-gray-700 dark:text-gray-300"
                        title={cat.description}
                      >
                        {cat.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border dark:divide-border-dark">
                  {filtered.map((client) => (
                    <tr
                      key={client.contactId}
                      className="hover:bg-surface-secondary dark:hover:bg-surface-dark-tertiary"
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {client.name}
                        </p>
                        {client.email && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">{client.email}</p>
                        )}
                      </td>
                      {NOTIFICATION_GATE_CATEGORIES.map((cat) => {
                        const enabled = isEnabled(client, cat.key);
                        const cellKey = `${client.contactId}:${cat.key}`;
                        return (
                          <td key={cat.key} className="px-3 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={enabled}
                              disabled={savingCell === cellKey}
                              onChange={(e) => void toggle(client, cat.key, e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700"
                              aria-label={`${enabled ? 'Disable' : 'Enable'} ${cat.label} notifications for ${client.name}`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="p-4 text-xs text-gray-500 dark:text-gray-400">
            Checked = client receives that category. Unchecked = suppressed for that client.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
